import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, Stamp, Lock, CheckCircle2, RotateCcw } from "lucide-react";
import StudioShell from "../components/studio/StudioShell";
import { ART_FORMS, getPassport, resetPassport } from "../data/artForms";
import { KolamGrid, KolamLoop } from "../components/Motifs";

export default function StudioHub() {
  const [passport, setPassport] = useState(getPassport());

  useEffect(() => {
    const handler = () => setPassport(getPassport());
    window.addEventListener("passport-update", handler);
    return () => window.removeEventListener("passport-update", handler);
    // Mount-only: setPassport is stable; handler reads getPassport at fire time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stampedCount = Object.keys(passport).length;
  const liveCount = ART_FORMS.filter((a) => a.status === "live").length;

  return (
    <StudioShell>
      {/* Hero */}
      <section className="relative overflow-hidden pt-16 pb-12 sm:pt-20 sm:pb-16">
        <div className="absolute top-12 right-4 w-56 h-56 text-[#D95D39] opacity-20 pointer-events-none kolam-bg">
          <KolamGrid cols={7} rows={7} className="w-full h-full" />
        </div>
        <div className="absolute -bottom-8 left-4 w-44 h-44 text-[#1E2A4F] opacity-10 pointer-events-none">
          <KolamLoop className="w-full h-full" />
        </div>
        <div className="max-w-6xl mx-auto px-6 sm:px-10 relative text-center">
          <span className="inline-flex items-center gap-2 text-[#D95D39]">
            <Sparkles size={18} />
            <span className="overline">Meera&apos;s Art Studio</span>
          </span>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#1E2A4F] mt-5 leading-[0.95] tracking-tight">
            Four art forms.
            <br />
            <em className="italic font-light">One little artist.</em>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#5C6A8F] max-w-2xl mx-auto leading-relaxed">
            Travel through India&apos;s living art traditions — learn the story, try the
            activity, save the artwork, and earn a stamp in your Art Passport.
          </p>
        </div>
      </section>

      {/* Art forms grid */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 pb-16" data-testid="art-forms-grid">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {ART_FORMS.map((art, i) => {
            const earned = !!passport[art.id];
            const isLive = art.status === "live";
            const card = (
              <motion.article
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`art-card-${art.id}`}
                className={`relative h-full rounded-2xl p-7 sm:p-8 border-2 transition-all duration-500 ${
                  isLive
                    ? "bg-white border-[#E5DFD3] hover:border-[#D95D39] hover:-translate-y-1 cursor-pointer"
                    : "bg-[#F3EDE4] border-[#E5DFD3] opacity-70"
                }`}
                style={isLive && earned ? { borderColor: art.accent } : {}}
              >
                {earned && (
                  <span
                    className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-[#F9F6F0]"
                    style={{ background: art.accent }}
                    data-testid={`art-card-${art.id}-stamp`}
                  >
                    <CheckCircle2 size={16} />
                  </span>
                )}
                {!isLive && (
                  <span className="absolute top-4 right-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#1E2A4F]/10 text-[#1E2A4F] text-[10px] tracking-[0.18em] uppercase">
                    <Lock size={11} /> Soon
                  </span>
                )}

                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center mb-5"
                  style={{ background: art.bg }}
                >
                  <span
                    className="font-display text-2xl"
                    style={{ color: art.accent }}
                  >
                    {art.name[0]}
                  </span>
                </div>

                <p
                  className="text-[10px] tracking-[0.24em] uppercase font-medium"
                  style={{ color: art.accent }}
                >
                  {art.region}
                </p>
                <h3 className="font-display text-3xl text-[#1E2A4F] mt-2 leading-tight">
                  {art.name}
                </h3>
                <p className="text-sm text-[#5C6A8F] mt-3 leading-relaxed">{art.blurb}</p>

                {isLive && (
                  <span
                    className="mt-6 inline-flex items-center gap-1 text-xs uppercase tracking-[0.22em]"
                    style={{ color: art.accent }}
                  >
                    Open studio <ArrowRight size={12} />
                  </span>
                )}
              </motion.article>
            );

            return isLive ? (
              <Link key={art.id} to={art.path} className="block h-full">
                {card}
              </Link>
            ) : (
              <div key={art.id} className="h-full">
                {card}
              </div>
            );
          })}
        </div>
      </section>

      {/* Art Passport */}
      <section
        id="passport"
        data-testid="art-passport"
        className="max-w-6xl mx-auto px-6 sm:px-10 pb-24 scroll-mt-24"
      >
        <div className="rounded-3xl bg-[#1E2A4F] text-[#F9F6F0] p-8 sm:p-12 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-[#D95D39]/25 blur-3xl pointer-events-none" />
          <div className="grid lg:grid-cols-12 gap-8 items-center relative">
            <div className="lg:col-span-5">
              <span className="inline-flex items-center gap-2 text-[#E76F51]">
                <Stamp size={18} />
                <span className="overline !text-[#E76F51]">Art Passport</span>
              </span>
              <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight">
                Collect a stamp from every art form.
              </h2>
              <p className="text-[#F9F6F0]/70 mt-4 leading-relaxed max-w-md">
                Finish each activity and save your artwork to earn a stamp. Your passport is
                saved on this device — come back any time to keep collecting.
              </p>
              <p className="mt-6 text-3xl font-display">
                {stampedCount} <span className="text-[#F9F6F0]/50 text-xl">/ {ART_FORMS.length}</span>
              </p>
              <p className="text-xs tracking-[0.24em] uppercase text-[#F9F6F0]/50">stamps earned</p>
              {stampedCount > 0 && (
                <button
                  onClick={() => {
                    if (window.confirm("Reset your art passport? This will erase all earned stamps.")) {
                      resetPassport();
                    }
                  }}
                  data-testid="passport-reset"
                  className="mt-6 inline-flex items-center gap-2 text-xs text-[#F9F6F0]/60 hover:text-[#F9F6F0] transition-colors"
                >
                  <RotateCcw size={12} /> Reset passport
                </button>
              )}
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {ART_FORMS.map((art) => {
                  const earned = !!passport[art.id];
                  return (
                    <div
                      key={art.id}
                      data-testid={`passport-stamp-${art.id}`}
                      className="aspect-square rounded-xl border-2 border-dashed flex items-center justify-center flex-col p-2 text-center"
                      style={{
                        background: earned ? art.bg : "transparent",
                        borderColor: earned ? art.accent : "rgba(249, 246, 240, 0.2)",
                        borderStyle: earned ? "solid" : "dashed",
                      }}
                    >
                      {earned ? (
                        <>
                          <Stamp size={22} style={{ color: art.accent }} />
                          <p
                            className="font-display text-base mt-2 leading-tight"
                            style={{ color: art.accent }}
                          >
                            {art.name}
                          </p>
                        </>
                      ) : (
                        <p className="text-[10px] tracking-[0.18em] uppercase text-[#F9F6F0]/40">
                          {art.name}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
              {stampedCount === ART_FORMS.length && (
                <p className="mt-6 text-center font-display italic text-2xl text-[#E76F51]">
                  Complete! You&apos;re a true young Indian artist 🌟
                </p>
              )}
              {stampedCount < liveCount && (
                <p className="mt-6 text-sm text-[#F9F6F0]/60 text-center">
                  {liveCount - stampedCount} stamp{liveCount - stampedCount === 1 ? "" : "s"} still
                  to earn — pick an art form above to begin.
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E5DFD3] py-10 text-center text-xs text-[#5C6A8F]">
        Part of{" "}
        <Link to="/" className="ink-link text-[#1E2A4F]">
          Divya&apos;s Story World
        </Link>
      </footer>
    </StudioShell>
  );
}
